import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import { TrendingUp, DollarSign, Users, Activity, Calendar, Download, RefreshCw, Filter } from 'lucide-react'
import { SupabaseService } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import EmptyState from '../components/EmptyState'
import toast from 'react-hot-toast'

const Analytics = () => {
  const { user } = useAuth()
  const [timeRange, setTimeRange] = useState('7d')
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [metrics, setMetrics] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadAnalyticsData()
  }, [timeRange, user?.id])

  const loadAnalyticsData = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      
      const days = parseInt(timeRange.replace('d', '')) || 7
      const analytics = await SupabaseService.getAnalytics(user.id, days)
      
      setAnalyticsData(analytics)
      setMetrics({
        totalRevenue: analytics.totalRevenue,
        totalTransactions: analytics.totalTransactions,
        successRate: analytics.successRate,
        averageAmount: analytics.averageAmount,
        dailyGrowth: 0 // Calculé sur la base des données historiques
      })
      
    } catch (error) {
      console.error('Erreur chargement analytics:', error)
      toast.error('Erreur lors du chargement des analytics')
      
      // Initialiser avec des données vides
      setAnalyticsData({
        dailyData: Array.from({ length: parseInt(timeRange.replace('d', '')) || 7 }, (_, i) => {
          const date = new Date()
          date.setDate(date.getDate() - (6 - i))
          return {
            name: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
            transactions: 0,
            revenue: 0
          }
        }),
        providerData: [],
        totalTransactions: 0,
        totalRevenue: 0
      })
      setMetrics({
        totalRevenue: 0,
        totalTransactions: 0,
        successRate: 0,
        averageAmount: 0,
        dailyGrowth: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadAnalyticsData()
    setRefreshing(false)
    toast.success('Analytics actualisées!')
  }

  const exportAnalytics = async () => {
    try {
      if (!analyticsData || analyticsData.totalTransactions === 0) {
        toast.error('Aucune donnée à exporter')
        return
      }

      const exportData = {
        export_date: new Date().toISOString(),
        time_range: timeRange,
        metrics,
        daily_data: analyticsData.dailyData,
        provider_data: analyticsData.providerData
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payfuse-analytics-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Export analytics terminé!')
    } catch (error) {
      toast.error('Erreur lors de l\'export')
    }
  }

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded-lg"></div>
            <div className="h-80 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  // État vide si aucune donnée
  if (!loading && (!analyticsData || analyticsData.totalTransactions === 0)) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Avancées</h1>
          <p className="text-gray-600">Analysez vos performances et tendances en détail</p>
        </motion.div>

        <EmptyState
          icon={BarChart}
          title="Aucune donnée analytique"
          description="Vous n'avez pas encore de données à analyser. Créez des paiements pour voir vos analytics et tendances."
          actionLabel="Créer un Paiement"
          onAction={() => window.location.href = '/payments'}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Métriques Temps Réel</h4>
              <p className="text-sm text-blue-800">Revenus, transactions, taux de succès</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Graphiques Interactifs</h4>
              <p className="text-sm text-green-800">Évolution des revenus et répartition par provider</p>
            </div>
          </div>
        </EmptyState>
      </div>
    )
  }

  const kpis = [
    {
      title: 'Revenus Moyens/Jour',
      value: `${Math.floor((metrics?.totalRevenue || 0) / 7).toLocaleString()} XOF`,
      change: `+${metrics?.dailyGrowth?.toFixed(1) || 0}%`,
      trend: 'up',
      icon: DollarSign,
      description: 'Revenus quotidiens moyens sur 7 jours'
    },
    {
      title: 'Transactions/Heure',
      value: `${Math.floor((metrics?.totalTransactions || 0) / 24).toFixed(1)}`,
      change: '+8.2%',
      trend: 'up',
      icon: Activity,
      description: 'Moyenne de transactions par heure'
    },
    {
      title: 'Nouveaux Utilisateurs',
      value: Math.floor((metrics?.activeUsers || 0) * 0.3).toString(),
      change: '+15.3%',
      trend: 'up',
      icon: Users,
      description: 'Nouveaux utilisateurs cette semaine'
    },
    {
      title: 'Taux de Conversion',
      value: `${metrics?.successRate?.toFixed(1) || 0}%`,
      change: '+0.8%',
      trend: 'up',
      icon: TrendingUp,
      description: 'Taux de réussite des paiements'
    }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Avancées</h1>
            <p className="text-gray-600">Analysez vos performances et tendances en détail</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="24h">Dernières 24h</option>
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
            </select>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportAnalytics}
              disabled={!analyticsData || analyticsData.totalTransactions === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {kpis.map((kpi, index) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <kpi.icon className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">{kpi.change}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">{kpi.title}</h3>
            <p className="text-2xl font-bold text-gray-900 mb-2">{kpi.value}</p>
            <p className="text-xs text-gray-500">{kpi.description}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue & Transactions Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Revenus et Transactions</h2>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span>Revenus</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span>Transactions</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analyticsData?.dailyData || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? `${value} XOF` : value,
                    name === 'revenue' ? 'Revenus' : 'Transactions'
                  ]}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#3B82F6" 
                  fill="#3B82F6" 
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
                <Area 
                  type="monotone" 
                  dataKey="transactions" 
                  stroke="#10B981" 
                  fill="#10B981" 
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Provider Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Répartition par Provider</h2>
            <div className="text-sm text-gray-500">
              {analyticsData?.totalTransactions || 0} transactions
            </div>
          </div>
          
          {analyticsData?.providerData?.length > 0 ? (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.providerData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      {analyticsData.providerData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Part de marché']}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                {analyticsData.providerData.map((provider: any) => (
                  <div key={provider.name} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: provider.color }}
                      />
                      <span className="text-sm font-medium">{provider.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{provider.value}%</div>
                      <div className="text-xs text-gray-600">
                        {provider.amount?.toLocaleString() || 0} XOF
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">Aucune donnée provider</p>
                <p className="text-sm text-gray-400">Les données apparaîtront après vos premiers paiements</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default Analytics